import { HttpParams } from '@angular/common/http';
import { AiEngine, CreateAiEngineDTO, UpdateAiEngineDTO } from 'services/ai-engine/ai-engine.model';
import { Capability, UpdateCapabilityDTO } from 'services/capability/capability.model';
import { CreateRoleDTO, Role, ROLE, UpdateRoleDTO } from 'services/role/role.model';
import { aiEnginesDb } from '../context/context.const';
import { MockHttpError, mockRoute, newMockId, nowIso, paginate } from '../mock-api';
import { MOCK_AUDIT_USER, MOCK_HUB_ROLES } from '../shared/mock-context.const';

/** Rotas usadas só pelas telas de admin do Hub: Motores de IA, Roles e Capabilities */

const AUDIT = {
	createdAt: '2026-01-10T12:00:00.000Z',
	createdBy: MOCK_AUDIT_USER,
	updatedAt: '2026-08-20T12:00:00.000Z',
	updatedBy: MOCK_AUDIT_USER,
};

function capability(id: string, slug: string, name: string, description: string): Capability {
	return { id, slug, name, description, ...AUDIT };
}

const capabilitiesDb: Capability[] = [
	capability(
		'cap-001',
		'jobs:read',
		'Visualizar jobs',
		'Acessar a caixa de entrada e o detalhe dos jobs',
	),
	capability('cap-002', 'jobs:create', 'Criar jobs', 'Enviar novos documentos para processamento'),
	capability(
		'cap-003',
		'jobs:retry',
		'Reprocessar jobs',
		'Reprocessar jobs finalizados ou com falha',
	),
	capability('cap-004', 'review:write', 'Revisar', 'Aprovar, rejeitar e concluir revisões'),
	capability('cap-005', 'dashboard:read', 'Ver dashboard', 'Acessar as métricas do produto'),
	capability(
		'cap-006',
		'settings:write',
		'Configurar produto',
		'Editar templates, prompts e base de conhecimento',
	),
	capability(
		'cap-007',
		'users:manage',
		'Gerenciar usuários',
		'Convidar e remover usuários do produto',
	),
	capability('cap-008', 'developer:manage', 'Desenvolvedor', 'Gerenciar chaves de API e webhook'),
	capability('cap-009', 'billing:read', 'Ver cobrança', 'Visualizar consumo e faixas de cobrança'),
];

const ROLE_CAPABILITIES: Record<string, string[]> = {
	[MOCK_HUB_ROLES[ROLE.ADMIN_MASTER].id]: capabilitiesDb.map((item) => item.id),
	[MOCK_HUB_ROLES[ROLE.BUSINESS_USER].id]: [
		'cap-001',
		'cap-002',
		'cap-003',
		'cap-004',
		'cap-005',
		'cap-006',
		'cap-007',
		'cap-008',
	],
	[MOCK_HUB_ROLES[ROLE.OPERATOR].id]: ['cap-001', 'cap-002', 'cap-004'],
};

const rolesDb: Role[] = Object.values(MOCK_HUB_ROLES).map((role) => ({ ...role }));

function withCapabilities(role: Role): Role {
	return {
		...role,
		capabilities: capabilitiesDb
			.filter((item) => (ROLE_CAPABILITIES[role.id] ?? []).includes(item.id))
			.map(({ id, name, slug, description }) => ({ id, name, slug, description })),
	};
}

function withRoles(item: Capability): Capability {
	return {
		...item,
		roles: rolesDb
			.filter((role) => (ROLE_CAPABILITIES[role.id] ?? []).includes(item.id))
			.map(({ id, name, description }) => ({ id, name, description })),
	};
}

/** Busca simples por `name`/`description`/`search` (sem acento e sem caixa) */
function matchesText(query: HttpParams, ...values: (string | null | undefined)[]): boolean {
	const term = query.get('search') ?? query.get('name') ?? query.get('description');
	if (!term) return true;
	const normalize = (value: string) =>
		value
			.normalize('NFD')
			.replace(/\p{Diacritic}/gu, '')
			.toLowerCase();
	return values.some((value) => value && normalize(value).includes(normalize(term)));
}

function findOr404<T extends { id: string }>(items: T[], id: string, label: string): T {
	const item = items.find((candidate) => candidate.id === id);
	if (!item) throw new MockHttpError(404, `${label} não encontrado(a)`);
	return item;
}

export const ADMIN_MOCK_ROUTES = [
	// ── Motores de IA ───────────────────────────────────────────────────────
	mockRoute('GET', '/ai-engines', ({ query }) => {
		const status = query.get('status') ?? 'ACTIVE';
		const items = aiEnginesDb
			.filter((engine) =>
				status === 'ALL' ? true : status === 'DELETED' ? !!engine.deletedAt : !engine.deletedAt,
			)
			.filter((engine) => matchesText(query, engine.name, engine.provider, engine.modelId));
		return paginate(items, query);
	}),
	mockRoute('GET', '/ai-engines/:id', ({ params }) =>
		findOr404(aiEnginesDb, params['id'], 'Motor de IA'),
	),
	mockRoute<CreateAiEngineDTO>('POST', '/ai-engines', ({ body }) => {
		const engine: AiEngine = {
			id: newMockId('engine'),
			description: body.description ?? null,
			...body,
			createdAt: nowIso(),
			createdBy: MOCK_AUDIT_USER,
			updatedAt: nowIso(),
			updatedBy: MOCK_AUDIT_USER,
			deletedAt: null,
			deletedBy: null,
		};
		aiEnginesDb.unshift(engine);
		return engine;
	}),
	mockRoute<UpdateAiEngineDTO>('PATCH', '/ai-engines/:id', ({ params, body }) => {
		const engine = findOr404(aiEnginesDb, params['id'], 'Motor de IA');
		Object.assign(engine, body, { updatedAt: nowIso(), updatedBy: MOCK_AUDIT_USER });
		return engine;
	}),
	mockRoute('DELETE', '/ai-engines/:id', ({ params }) => {
		const engine = findOr404(aiEnginesDb, params['id'], 'Motor de IA');
		Object.assign(engine, { deletedAt: nowIso(), deletedBy: MOCK_AUDIT_USER });
		return null;
	}),
	mockRoute('POST', '/ai-engines/:id/restore', ({ params }) => {
		const engine = findOr404(aiEnginesDb, params['id'], 'Motor de IA');
		Object.assign(engine, { deletedAt: null, deletedBy: null, updatedAt: nowIso() });
		return engine;
	}),

	// ── Roles ───────────────────────────────────────────────────────────────
	mockRoute('GET', '/role', ({ query }) =>
		paginate(
			rolesDb
				.filter((role) => matchesText(query, role.name, role.description))
				.map(withCapabilities),
			query,
		),
	),
	mockRoute('GET', '/role/:id', ({ params }) =>
		withCapabilities(findOr404(rolesDb, params['id'], 'Role')),
	),
	mockRoute<CreateRoleDTO>('POST', '/role', ({ body }) => {
		if (rolesDb.some((role) => role.name.toLowerCase() === body.name.toLowerCase())) {
			throw new MockHttpError(409, 'Role já existe', `Já existe uma role chamada "${body.name}".`);
		}
		const role: Role = {
			id: newMockId('role'),
			name: body.name,
			slug: body.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
			description: body.description,
			...AUDIT,
			createdAt: nowIso(),
			updatedAt: nowIso(),
		};
		rolesDb.push(role);
		ROLE_CAPABILITIES[role.id] = body.capabilities;
		return withCapabilities(role);
	}),
	mockRoute<UpdateRoleDTO>('PATCH', '/role/:id', ({ params, body }) => {
		const role = findOr404(rolesDb, params['id'], 'Role');
		Object.assign(role, { name: body.name, description: body.description, updatedAt: nowIso() });
		ROLE_CAPABILITIES[role.id] = body.capabilities;
		return withCapabilities(role);
	}),
	mockRoute('DELETE', '/role/:id', ({ params }) => {
		const index = rolesDb.findIndex((role) => role.id === params['id']);
		if (index < 0) throw new MockHttpError(404, 'Role não encontrada');
		if (Object.values(MOCK_HUB_ROLES).some((role) => role.id === params['id'])) {
			throw new MockHttpError(409, 'Role em uso', 'Roles do sistema não podem ser removidas.');
		}
		rolesDb.splice(index, 1);
		return null;
	}),

	// ── Capabilities ────────────────────────────────────────────────────────
	mockRoute('GET', '/capability', ({ query }) =>
		paginate(
			capabilitiesDb
				.filter((item) => matchesText(query, item.name, item.description, item.slug))
				.map(withRoles),
			query,
		),
	),
	mockRoute('GET', '/capability/:id', ({ params }) =>
		withRoles(findOr404(capabilitiesDb, params['id'], 'Capability')),
	),
	mockRoute<UpdateCapabilityDTO>('PATCH', '/capability/:id', ({ params, body }) => {
		const item = findOr404(capabilitiesDb, params['id'], 'Capability');
		Object.assign(item, body, { updatedAt: nowIso() });
		return withRoles(item);
	}),
];
